import { useEffect, useMemo, useState } from 'react';
import { Calendar, MapPin, Users, Search, Filter, CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';
import { bookingService } from '../../services/bookingService';
import { paymentService } from '../../services/paymentService';
import { UI, buttonStyle, ui } from '../../utils/ui';

const statusPill = (status) => {
  const map = {
    confirmed: { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#86efac' },
    pending: { backgroundColor: '#fef9c3', color: '#854d0e', borderColor: '#fde047' },
    completed: { backgroundColor: '#dbeafe', color: '#1e40af', borderColor: '#93c5fd' },
    cancelled: { backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#fecaca' },
  };
  const c = map[status] || map.pending;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    borderRadius: 9999,
    fontSize: 11,
    fontWeight: 900,
    border: `1px solid ${c.borderColor}`,
    backgroundColor: c.backgroundColor,
    color: c.color,
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  };
};

const spinner = (color = UI.colors.primary) => ({
  width: 14,
  height: 14,
  border: `2px solid rgba(255,255,255,0.35)`,
  borderTopColor: color,
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
});

const paymentPill = (status) => {
  const map = {
    pending:  { backgroundColor: '#fef3c7', color: '#d97706', borderColor: '#fde68a' },
    paid:     { backgroundColor: '#dcfce7', color: '#16a34a', borderColor: '#86efac' },
    failed:   { backgroundColor: '#fee2e2', color: '#dc2626', borderColor: '#fecaca' },
    refunded: { backgroundColor: '#dbeafe', color: '#2563eb', borderColor: '#93c5fd' },
  };
  const c = map[status] || map.pending;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    borderRadius: 9999,
    fontSize: 11,
    fontWeight: 900,
    border: `1px solid ${c.borderColor}`,
    backgroundColor: c.backgroundColor,
    color: c.color,
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  };
};

const MyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [cancelling, setCancelling] = useState(null);
  const [payingNow, setPayingNow] = useState(null);

  useEffect(() => {
    bookingService.getMyBookings()
      .then((res) => setBookings(res.data.bookings || []))
      .catch(() => toast.error('Failed to load bookings.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (document.querySelector('script[src*="checkout.razorpay"]')) return;
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    document.body.appendChild(s);
  }, []);

  const handlePayNow = async (b) => {
    setPayingNow(b.id);
    try {
      const orderRes = await paymentService.createOrder(b.id);
      const { order_id, amount, currency, key_id, booking_reference, prefill } = orderRes.data;

      if (!window.Razorpay) {
        toast.error('Payment gateway not loaded. Please refresh and try again.');
        setPayingNow(null);
        return;
      }

      new window.Razorpay({
        key: key_id, amount, currency, order_id,
        name: 'Burans Hikes Home Stay',
        description: booking_reference,
        prefill,
        theme: { color: '#16a34a' },
        handler: async (response) => {
          try {
            await paymentService.verifyPayment({
              booking_id:          b.id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_signature:  response.razorpay_signature,
            });
            setBookings((prev) => prev.map((x) =>
              x.id === b.id ? { ...x, payment_status: 'paid', booking_status: 'confirmed' } : x
            ));
            toast.success('Payment successful! Booking confirmed.');
          } catch (err) {
            toast.error(err.response?.data?.message || 'Verification failed. Contact support.');
          } finally {
            setPayingNow(null);
          }
        },
        modal: {
          ondismiss: () => {
            toast.error('Payment cancelled. You can try again anytime.');
            setPayingNow(null);
          },
        },
      }).open();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to initiate payment.');
      setPayingNow(null);
    }
  };

  const doCancel = async (id) => {
    setCancelling(id);
    try {
      await bookingService.cancelBooking(id);
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, booking_status: 'cancelled' } : b)));
      toast.success('Booking cancelled.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel booking.');
    } finally {
      setCancelling(null);
    }
  };

  const handleCancel = (id) => {
    toast((t) => (
      <div>
        <p style={{ margin: '0 0 10px', fontWeight: 900, color: UI.colors.text }}>Cancel this booking?</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => { toast.dismiss(t.id); doCancel(id); }}
            style={{ ...buttonStyle('danger'), padding: '8px 12px', borderRadius: 12 }}
          >
            Yes, Cancel
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{ ...buttonStyle('outline'), padding: '8px 12px', borderRadius: 12 }}
          >
            Keep
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return bookings.filter((b) => {
      const matchSearch = (b.booking_reference || '').toLowerCase().includes(q) || (b.room_name || '').toLowerCase().includes(q);
      const matchStatus = filterStatus === 'all' || b.booking_status === filterStatus;
      return matchSearch && matchStatus;
    });
  }, [bookings, filterStatus, searchTerm]);

  const wrapStyle = { display: 'flex', flexDirection: 'column', gap: 18 };

  return (
    <div style={wrapStyle}>
      {/* Header */}
      <div>
        <h1 style={{ fontFamily: UI.font.heading, fontSize: 30, fontWeight: 900, margin: 0, color: UI.colors.text }}>My Bookings</h1>
        <p style={{ margin: '8px 0 0', fontSize: 14, color: UI.colors.muted }}>View and manage all your reservations</p>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 260, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 14, border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#ffffff' }}>
          <Search size={16} color="#9ca3af" />
          <input
            type="text"
            placeholder="Search by booking ref or room name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', border: 'none', outline: 'none', fontSize: 14, backgroundColor: 'transparent', color: UI.colors.text }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 14, border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#ffffff' }}>
          <Filter size={16} color="#9ca3af" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ border: 'none', outline: 'none', backgroundColor: 'transparent', fontSize: 14, fontWeight: 800, color: UI.colors.text, cursor: 'pointer' }}
          >
            <option value="all">All Status</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px 0' }}>
          <div style={{ width: 36, height: 36, border: `3px solid ${UI.colors.border}`, borderTopColor: UI.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ ...ui.card, textAlign: 'center', padding: '56px 18px' }}>
          <Calendar size={48} color="#9ca3af" style={{ opacity: 0.25, margin: '0 auto 10px' }} />
          <p style={{ margin: 0, color: UI.colors.muted, fontSize: 16, fontWeight: 900 }}>No bookings found</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
          {filtered.map((b) => (
            <div key={b.id} style={{ ...ui.card, overflow: 'hidden' }}>
              <div style={{ position: 'relative', height: 176, backgroundColor: '#f3f4f6' }}>
                <img
                  src={b.room_images?.[0] || 'https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=400&h=300&fit=crop'}
                  alt={b.room_name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                  <span style={statusPill(b.booking_status)}>{b.booking_status}</span>
                  {b.payment_status && <span style={paymentPill(b.payment_status)}>{b.payment_status}</span>}
                </div>
              </div>
              <div style={{ padding: 18 }}>
                <p style={{ margin: 0, fontFamily: UI.font.heading, fontSize: 18, fontWeight: 900, color: UI.colors.text }}>{b.room_name}</p>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: '#9ca3af', fontWeight: 800 }}>Ref: {b.booking_reference}</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14, marginBottom: 14, color: UI.colors.muted, fontSize: 13 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Calendar size={14} color={UI.colors.primary} />
                    <span style={{ color: UI.colors.text, fontWeight: 700 }}>{b.check_in} → {b.check_out}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <MapPin size={14} color={UI.colors.primary} />
                    <span style={{ color: UI.colors.text, fontWeight: 700 }}>Room {b.room_number}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Users size={14} color={UI.colors.primary} />
                    <span style={{ color: UI.colors.text, fontWeight: 700 }}>{b.guests} Guests</span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingTop: 14, borderTop: `1px solid ${UI.colors.border}` }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Total Amount</p>
                    <p style={{ margin: '6px 0 0', fontFamily: UI.font.heading, fontSize: 20, fontWeight: 900, color: UI.colors.primary }}>₹{Number(b.total_amount).toLocaleString()}</p>
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {b.payment_status === 'pending' && b.booking_status === 'pending' && b.customer_id && (
                      <button
                        onClick={() => handlePayNow(b)}
                        disabled={payingNow === b.id}
                        style={{
                          ...buttonStyle('primary'),
                          padding: '10px 12px',
                          borderRadius: 14,
                          opacity: payingNow === b.id ? 0.75 : 1,
                          display: 'flex', alignItems: 'center', gap: 6,
                          backgroundColor: '#d97706', borderColor: '#d97706',
                        }}
                      >
                        {payingNow === b.id ? <span style={spinner('#ffffff')} /> : <CreditCard size={14} />}
                        {payingNow === b.id ? 'Processing…' : 'Pay Now'}
                      </button>
                    )}
                    {(b.booking_status === 'confirmed' || b.booking_status === 'pending') && (
                      <button
                        onClick={() => handleCancel(b.id)}
                        disabled={cancelling === b.id}
                        style={{
                          ...buttonStyle('outline'),
                          borderColor: '#fecaca',
                          color: UI.colors.danger,
                          width: 120,
                          padding: '10px 12px',
                          borderRadius: 14,
                          opacity: cancelling === b.id ? 0.75 : 1,
                        }}
                      >
                        {cancelling === b.id ? <span style={spinner(UI.colors.danger)} /> : null}
                        {cancelling === b.id ? 'Cancelling' : 'Cancel'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyBookings;

